// angular
import { Component, effect, inject, input } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { NgClass } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { RecaptchaModule } from 'ng-recaptcha';
import { ActivatedRoute } from '@angular/router';
// components
import { ButtonComponent } from '@app/core/components/button/button.component';
import { InputComponent } from '@app/core/components/input/input.component';
import { SectionTabsComponent } from '@app/core/components/section-tabs/section-tabs.component';
import { MapBoxComponent } from '@app/core/components/map-box/map-box.component';
import { PhoneNumberComponent } from '@app/core/components/phone-number/phone-number.component';
// services
import { AboutService } from '../../shared/services/about.service';
import { RecaptchaService } from '@app/shared/services/recaptcha.service';
import { PwjToastService } from '@app/core/shared/services/app-toast.service';
// types
import { ISectionTab } from '@app/core/shared/interfaces/section-tab.interface';
import { ContactInfo } from '@app/+about/shared/types/contact-info.type';
import { LocPosition } from '@app/+about/shared/types/lock-position.type';
import { RecaptchaData } from '@app/shared/types/recaptcha-data.type';
// mock data
import { contactTabs } from '@app/+about/shared/mock/contact-page';
// pipes & env
import { TranslateDataPipe } from '@app/shared/pipes/translate-data.pipe';
import { environment } from '@environments/environment';
import { PortfolioService } from '@app/+portfolio/shared/services/portfolio.service';
import { IBusinessContactForm } from '@app/+portfolio/shared/interfaces/business-contact-form.interface';

@Component({
  selector: 'pwj-contact-section',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    SectionTabsComponent,
    InputComponent,
    ButtonComponent,
    TranslateModule,
    TranslateDataPipe,
    NgClass,
    MapBoxComponent,
    PhoneNumberComponent,
    RecaptchaModule,
  ],
  providers: [AboutService],
  templateUrl: './contact-section.component.html',
  styleUrl: './contact-section.component.scss',
})
export class ContactSectionComponent {
  // tabs data
  public tabs: ISectionTab[] = contactTabs;
  // form property
  public contactForm!: FormGroup;
  public isDisabledSubmitBtn: boolean = false;

  public companyLocation: LocPosition = {
    lat: Number('32.65645419360533'),
    lng: Number('51.63953781761789'),
  };

  // inputs
  showTab = input<boolean>(true);
  isPortfolioMode = input<boolean>(false);
  contactData = input<ContactInfo[]>();
  businessId = input<string>();

  private readonly _aboutService = inject(AboutService);
  private readonly _portfolioService = inject(PortfolioService);
  private readonly _fb = inject(FormBuilder);
  private readonly _recaptchaService = inject(RecaptchaService);
  private readonly _toastService = inject(PwjToastService);

  constructor() {
    this._recaptchaService.recaptchaValidation('contact_us');

    this._buildForm();

    effect(() => {
      if (this._recaptchaService.recaptchaData() !== null) {
        const captchaData =
          this._recaptchaService.recaptchaData() as RecaptchaData;

        if (captchaData?.score < environment.recaptcha.targetScore) {
          this.isDisabledSubmitBtn = true;
        }
      }
    });

    this.contactForm.valueChanges.subscribe(_ => {
      if (!this.contactForm.dirty) {
        this.isDisabledSubmitBtn = true;
      }
    });
  }

  // build contact form
  private _buildForm() {
    this.contactForm = this._fb.group({
      name: new FormControl(''),
      mobile: new FormControl(''),
      email: new FormControl(''),
      description: new FormControl(null, Validators.required),
    });
  }

  public submitContactForm() {
    // check form is valid or no
    if (!this.contactForm.valid) {
      this.contactForm.markAllAsTouched();
      return;
    }

    // get form values
    const formData = this.contactForm.value;
    // post data
    if (this.isPortfolioMode() && this?.businessId()) {
      const finalData: IBusinessContactForm = {
        message: formData?.description,
        email: formData?.email || null,
        mobile: formData?.mobile || null,
        name: formData?.name || null,
        business_id: this?.businessId() as string,
      };

      this._portfolioService
        .submitBusinessContactForm(finalData)
        .subscribe(res => {
          if (res) {
            this.contactForm.reset();
            this.contactForm.markAsUntouched();
            Object.keys(this.contactForm.controls).forEach(field => {
              const formControl = this.contactForm.controls[field];
              formControl.setErrors(null);
            });

            this._toastService.success(
              'toastMessages.messageSubmitted',
              'toastMessages.successTitle'
            );
          }
        });
    } else {
      this._aboutService.submitContactFormData(formData).subscribe(res => {
        if (res) {
          this.contactForm.reset();
          this.contactForm.markAsUntouched();
          Object.keys(this.contactForm.controls).forEach(field => {
            const formControl = this.contactForm.controls[field];
            formControl.setErrors(null);
          });

          this._toastService.success(
            'toastMessages.messageSubmitted',
            'toastMessages.successTitle'
          );
        }
      });
    }
  }
}
